import moment from 'moment';
import type { CoreTypes } from '../../core';
import { getDayIndex, getVertexId } from '../../core/util';
import type { BaseOptions } from '../../common/types';


type OutEvent = {
  id?:         string;
  externalid?: string;
  groupid?:    string;
  group?:      string;
  dayid?:      number;
  startTime?:  string;
  length?:     number;
  subject?:    string;
  roomid?:     string;
  room?:       string;
  teacherid?:  string;
  teacher?:    string;
  classid?:    string;
  class?:      string;
  inweek?:     string;
};

type Schedule = {
  division:  CoreTypes.Mixed.Division;
  settings:  CoreTypes.Mixed.Settings;
  periods:   CoreTypes.Mixed.Period[];
  locations: CoreTypes.Mixed.Location[];
  groups:    CoreTypes.Mixed.Group[];
  teachers:  CoreTypes.Mixed.Teacher[];
  courses:   CoreTypes.Mixed.Course[];
  events:    CoreTypes.Mixed.Event[];
};

export default (
  schedule: Schedule,
  options:  BaseOptions
): string => {

  // a map of period.id -> period
  const periodsMap = new Map(schedule.periods.map(x => [getVertexId(x, options) as string | undefined, x as Pick<typeof x, 'ranges'>] as const));
  periodsMap.set(undefined, { ranges: [{ start: schedule.division.start, end: schedule.division.end }] });

  // a map of period.id -> inWeeks
  const inWeeksMap = new Map<string | undefined, string>();
  periodsMap.forEach((period, id) => {
    const weeks = period.ranges
      .map(x => {
        // fetch all weeks between start and end
        const start = moment.utc(x.start);
        const end   = moment.utc(x.end);
        const weeks = new Array<number>();
        while (start.isBefore(end)) {
          weeks.push(start.week());
          start.add(1, 'week');
        }
        return weeks;
      })
      .flat()
      .join(', ');

    inWeeksMap.set(id, weeks);
  });

  // a map of location.id -> location
  const locationsMap = new Map(schedule.locations.map(x => [getVertexId(x, options), x] as const));

  // a map of group.id -> group
  const groupsMap = new Map(schedule.groups.map(x => [getVertexId(x, options), x] as const));

  // a map of teacher.id -> teacher
  const teachersMap = new Map(schedule.teachers.map(x => [getVertexId(x, options), x] as const));

  // a map of course.id -> course
  const coursesMap = new Map(schedule.courses.map(x => [getVertexId(x, options), x] as const));


  const events = schedule.events
    .map(event => {
      // ignore parked events
      if (event.parked) return;
      if (!event.start || !event.end) return;

      // must have a course
      const course = event.course ? coursesMap.get(getVertexId(event.course, options)) : undefined;
      if (!course) return;

      const start = moment.utc(event.start);
      const end   = moment.utc(event.end);

      const period = event.period ?? course.period ?? schedule.settings.period;
      const periodId = period ? getVertexId(period, options) : undefined;
      const inWeeks = inWeeksMap.get(periodId);

      const locations = (event.inLocations                 ?? []).map(x =>  x ? locationsMap.get(getVertexId(x,    options)) : null).filter((x): x is NonNullable<typeof x> => !!x);
      const teachers  = (event.teachers ?? course.teachers ?? []).map(x =>      teachersMap .get(getVertexId(x.to, options))       ).filter((x): x is NonNullable<typeof x> => !!x);
      const groups    = (event.groups   ?? course.groups   ?? []).map(x =>      groupsMap   .get(getVertexId(x.to, options))       ).filter((x): x is NonNullable<typeof x> => !!x);

      const out: OutEvent = {
        id:         getVertexId(event, options),
        externalid: event.ids?.toString(),
        groupid:    course.ids,
        group:      course.displayName,
        dayid:      getDayIndex(start),
        startTime:  start.format('HHmm'),
        length:     end.diff(start, 'minutes'),
        subject:    course.subject,
        inweek:     inWeeks,
        roomid:     locations.map(x => x.ids        ).join(', '),
        room:       locations.map(x => x.displayName).join(', '),
        teacherid:  teachers .map(x => x.ids        ).join(', '),
        teacher:    teachers .map(x => x.displayName).join(', '),
        classid:    groups   .map(x => x.ids        ).join(', '),
        class:      groups   .map(x => x.displayName).join(', ')
      };
      return out;
    })
    .filter(x => x != null);


  return toCsv(events);
};

function toCsv (events: OutEvent[]): string {
  // CSV header
  const headers = [
    'id', 'externalid', 'groupid', 'group', 'dayid',
    'startTime', 'length', 'subject', 'roomid', 'room',
    'teacherid', 'teacher', 'classid', 'class', 'inweek'
  ];

  // Convert events to CSV rows
  const csvRows = events.map(event =>
    headers.map(header => {
      const value = event[header as keyof OutEvent];
      // Handle null/undefined values
      if (value == null) return '';

      const stringValue = value.toString();
      // Escape values that contain semicolons, quotes, or newlines
      if (stringValue.includes(';') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(';')
  );

  // Combine header and rows with proper line endings
  const csv = [headers.join(';'), ...csvRows].join('\r\n');

  return csv;
}